import styles from './merge-contracts-dialog.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { useContext, useState } from 'react';
import { Checkbox, CreateHeaders, currencyHeader, dateHeader, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, MenuItem, PrimaryButton, Select, SnackBarContext, textHeader } from '@frontend-workspace/react-shared-lib';
import { IAllContracts } from '../../_models/all-contracts';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { useMakeRequestHook } from '../../requestUtils';

/* eslint-disable-next-line */
export interface MergeContractsDialogProps {
  open: boolean,
  onClose: () => void,
  allContracts: IAllContracts[]
}

export function MergeContractsDialog(props: MergeContractsDialogProps) {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [targetContractNumber, setTargetContractNumber] = useState<string | number>("");
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);

  const handleTargetContractNumberChange = (event: any) => setTargetContractNumber(event.target.value);

  const selectContractRenderer = (params: ValueFormatterParams) => {
    const handleChange = (event: any, checked: boolean) => checked ? setSelectedContracts(selectedContracts.concat(params.value)) :
      setSelectedContracts(selectedContracts.filter(c => c !== params.value));
    return (
      <Checkbox
        checked={selectedContracts.includes(params.value)}
        onChange={handleChange}
      />
    );
  };

  const handleCombineContracts = () => {
    const body = {
      contractNumbers: selectedContracts,
      moveCost: 0,
      targetContractNumber
    };
    if (targetContractNumber) {
      makeRequest(`contracts/${targetContractNumber}/action/combineContracts`, 'PUT', body).then(result => {
        setSnackbarMessage({ content: t("mergeContracts.combinesContracts"), type: "info" });
        props.onClose();
      });
    } else {
      if (!targetContractNumber) {
        setSnackbarMessage({ content: t("mergeContracts.targetContractNotSelected"), type: "error" });
      }
    }
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t("standard.mergeContracts")}
      </DialogTitle>
      <DialogContent>
        <div className="d-flex flex-row">
          <div className="mb-3">
            <Select
              displayEmpty
              value={targetContractNumber}
              onChange={handleTargetContractNumberChange}
              fullWidth
              sx={{ width: '300px' }}
              label={t("mergeContracts.selectTargetContract")}
              name='targetContractNumber'>
              {props.allContracts.map(p => (
                <MenuItem key={p.contractNumber} value={p.contractNumber}>{p.contractNumber}</MenuItem>
              ))}
            </Select>
          </div>
        </div>
        <WebkuaTable
          headers={CreateHeaders([
            textHeader("contractNumber", "", HeaderSizeEnum.SMALL, { renderer: selectContractRenderer }),
            textHeader("contractNumber", t("standard.contractNo"), HeaderSizeEnum.MEDIUM),
            textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.MEDIUM),
            textHeader("invoiceInterval", t("standard.duration"), HeaderSizeEnum.MEDIUM, {
              cellStyle: { justifyContent: 'right' },
              headerClass: "ag-right-aligned-header"
            }),
            dateHeader("nextInvoiceDate", t("standard.nextInvoice")),
            currencyHeader("nextInvoiceCost", t("standard.amount"), HeaderSizeEnum.LARGE, {
              cellStyle: { justifyContent: 'right' },
              headerClass: "ag-right-aligned-header"
            })
          ])}
          data={props.allContracts}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant="contained"
          size='large'
          onClick={handleCombineContracts}
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          variant="outlined"
          size='large'
          onClick={props.onClose}
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default MergeContractsDialog;